/**
 * Approximate Speech Transmission Index (STI) — derived entirely from the
 * octave-band C50 data already computed for the Clarity chart (clarity.ts),
 * no new backend computation.
 *
 * This is a well-known SIMPLIFIED estimate, not a true modulation-transfer-
 * function STI (IEC 60268-16): per-band STI ~= clamp((C50_dB + 15) / 30, 0, 1),
 * combined across the standard 7 octave bands with published approximate
 * band-importance weights. Good enough to flag "this room is clearly fine /
 * clearly bad for speech intelligibility", not a certified STI measurement —
 * EASE's real STI computation integrates the full modulation transfer
 * function per band, which needs the impulse response's envelope at 14
 * modulation frequencies, not just C50. Labelled "Approx. STI" everywhere
 * this surfaces for exactly that reason.
 *
 * The ray-traced decay curve (RT60DecayCurve) only covers >=200 Hz (see its
 * own doc comment) — the STI 125 Hz band, and 250 Hz if the room's decay
 * curve doesn't extend that low, are reported as uncovered rather than
 * silently substituting a mismatched nearby band. `coveredWeight` tells the
 * caller how much of the standard STI weighting was actually usable.
 */
import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, logFreqToX, type LinearScale, type LogFreqScale } from './scales';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import { computeC50 } from './clarity';
import type { RT60DecayCurve } from './registry';

/** Approximate octave-band importance weights for a simplified STI estimate
 *  (sums to 1.0). Standard STI octave centers, 125 Hz-8 kHz. */
export const STI_OCTAVE_WEIGHTS: { hz: number; weight: number }[] = [
  { hz: 125, weight: 0.13 },
  { hz: 250, weight: 0.14 },
  { hz: 500, weight: 0.11 },
  { hz: 1000, weight: 0.12 },
  { hz: 2000, weight: 0.19 },
  { hz: 4000, weight: 0.17 },
  { hz: 8000, weight: 0.14 },
];

export interface StiBandResult {
  /** Standard STI octave center this row represents. */
  targetHz: number;
  /** The decay curve's actual band frequency used, or null if this STI
   *  octave falls below the decay curve's coverage (see module header). */
  matchedHz: number | null;
  c50Db: number | null;
  /** Per-band approximate STI, clamped to [0, 1], or null if uncovered. */
  sti: number | null;
}

export interface StiResult {
  /** Weighted average over covered bands only, or null if nothing is
   *  covered. Renormalised by `coveredWeight`, so a partially-covered room
   *  still gets a meaningful (if less complete) number rather than being
   *  dragged down by missing low-frequency bands it can't be penalised for
   *  not having data on. */
  overall: number | null;
  bands: StiBandResult[];
  /** Sum of STI_OCTAVE_WEIGHTS actually used, in [0, 1]. Well under 1 means
   *  `overall` is missing real low-frequency coverage — read it as
   *  indicative, not complete. */
  coveredWeight: number;
}

export function computeApproxSTI(decay: RT60DecayCurve): StiResult {
  const dt = decay.timeS.length >= 2 ? decay.timeS[1]! - decay.timeS[0]! : 0.01;
  const minBandHz = decay.bandHz.length ? Math.min(...decay.bandHz) : Infinity;

  const bands: StiBandResult[] = STI_OCTAVE_WEIGHTS.map(({ hz }) => {
    if (hz < minBandHz) {
      return { targetHz: hz, matchedHz: null, c50Db: null, sti: null };
    }
    let matchedIdx = 0;
    let bestD = Infinity;
    decay.bandHz.forEach((bhz, i) => {
      const d = Math.abs(Math.log2(bhz / hz));
      if (d < bestD) { bestD = d; matchedIdx = i; }
    });
    const matchedHz = decay.bandHz[matchedIdx]!;
    const curve = decay.edcDb[matchedIdx] ?? [];
    const c50Db = computeC50(curve, dt);
    const sti = c50Db == null ? null : Math.max(0, Math.min(1, (c50Db + 15) / 30));
    return { targetHz: hz, matchedHz, c50Db, sti };
  });

  let weightedSum = 0;
  let coveredWeight = 0;
  bands.forEach((b, i) => {
    if (b.sti == null) return;
    const weight = STI_OCTAVE_WEIGHTS[i]!.weight;
    weightedSum += b.sti * weight;
    coveredWeight += weight;
  });

  return {
    overall: coveredWeight > 0 ? weightedSum / coveredWeight : null,
    bands,
    coveredWeight,
  };
}

// ── Geometry — a per-band bar chart (log-x, linear 0..1 y), mirrors the
// modalDensity.ts / rta.ts bar-chart shape ──────────────────────────────

export interface StiBar {
  targetHz: number;
  matchedHz: number | null;
  sti: number | null;
  x0: number;
  x1: number;
  /** Plot-local y of the bar's top; bar spans [y, frame.plotH]. Equals
   *  frame.plotH (zero height) when sti is null (no data). */
  y: number;
}

export interface StiChartInput {
  decay: RT60DecayCurve;
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface StiChartGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  bars: StiBar[];
  overall: number | null;
  coveredWeight: number;
  xTicks: AxisTicks;
  yTicks: YTick[];
}

export function computeStiChartGeometry(opts: StiChartInput): StiChartGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 100;
  const fMax = opts.fMax ?? 10000;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const yScale = createLinearScale(frame.plotH, 0, 1, true);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const result = computeApproxSTI(opts.decay);

  if (!opts.decay.bandHz.length || frame.plotW <= 0) {
    return {
      empty: true, frame, fMin, fMax, xScale, yScale, bars: [],
      overall: null, coveredWeight: 0, xTicks, yTicks: [],
    };
  }

  const barWidthFrac = 0.6;
  const bars: StiBar[] = result.bands.map(({ targetHz, matchedHz, sti }) => {
    const cx = logFreqToX(xScale, targetHz);
    // Half-width in px derived from spacing to neighbouring octave centers
    // in log-space, same convention rta.ts/modalDensity.ts bars use.
    const halfW = (frame.plotW / (Math.log2(fMax / fMin))) * (barWidthFrac / 2);
    return {
      targetHz,
      matchedHz,
      sti,
      x0: cx - halfW,
      x1: cx + halfW,
      y: sti == null ? frame.plotH : linearToPlot(yScale, sti),
    };
  });

  const yTicks: YTick[] = linearTicks(0, 1, 0.25).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: v === 0.5, label: v.toFixed(2),
  }));

  return {
    empty: false, frame, fMin, fMax, xScale, yScale, bars,
    overall: result.overall, coveredWeight: result.coveredWeight,
    xTicks, yTicks,
  };
}
